import { useParams } from 'react-router-dom'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Page, Layout, Card, BlockStack, Banner, Spinner, Text, Link } from '@shopify/polaris'
import { apiGet } from '../lib/api'

import { useDraftOrder } from '../hooks/useDraftOrder'
import { usePayment } from '../hooks/usePayment'
import { useMetafield } from '../hooks/useMetafield'
import { PaymentStatus } from '../components/PaymentStatus'
import { OrderSummary } from '../components/OrderSummary'
import { ActionButtons } from '../components/ActionButtons'
import { ValidationBanner } from '../components/ValidationBanner'
import { AmountDriftBanner } from '../components/AmountDriftBanner'
import { validateB2BFields, buildPaymentRequestBody, buildConfirmBody } from '../lib/validation'
import { adminUrl } from '../lib/admin-url'
import { isConfirmable, isCapturable, needsPolling, isCancellable, TERMINAL_SUCCESS, TERMINAL_FAILURE } from '../lib/status'
import { completeDraftOrder, writePaymentNoteAttribute, writeOrderBnplxAttributes, fetchDraftOrder, parseMetafield, type PaymentMetafield } from '../lib/graphql'

export default function DraftOrderDetail() {
    const { id } = useParams<{ id: string }>()
    const draftOrderGid = `gid://shopify/DraftOrder/${id}`

    const { draftOrder, payment: initialPayment, loading, error: loadError, refresh } = useDraftOrder(id!)
    const paymentApi = usePayment()
    const metafield = useMetafield(draftOrderGid)

    const [payment, setPayment] = useState<PaymentMetafield | null>(null)
    const [activeAction, setActiveAction] = useState<string | null>(null)
    const [message, setMessage] = useState<{ tone: 'success' | 'warning' | 'critical'; text: string } | null>(null)
    const [validationErrors, setValidationErrors] = useState<string[]>([])
    const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null)
    const [proxyUrl, setProxyUrl] = useState<string | null>(null)
    const [autoAuthorize, setAutoAuthorize] = useState(false)
    const [autoRequesting, setAutoRequesting] = useState(false)

    // Sync initial payment from metafield
    useEffect(() => {
        if (initialPayment) setPayment(initialPayment)
    }, [initialPayment])

    // Latest payment, readable inside the auto-poll timer without re-subscribing.
    const paymentRef = useRef(payment)
    paymentRef.current = payment

    // Is auto-authorize enabled for this shop? Needed to know whether a
    // guarantee-less draft is being authorized server-side right now.
    useEffect(() => {
        apiGet<{ config: { autoAuthorize?: boolean } | null }>('/api/settings')
            .then(r => setAutoAuthorize(!!r.config?.autoAuthorize))
            .catch(() => {})
    }, [])

    // With auto-authorize ON, the guarantee is requested server-side on the
    // draft_orders webhook. Reflect that in-flight request as "Requesting
    // Guarantee" and poll for it to land — bounded so it never spins forever
    // (an incomplete/skipped draft will surface its status via the poll).
    useEffect(() => {
        if (!autoAuthorize || paymentRef.current) return
        setAutoRequesting(true)
        let cancelled = false
        let attempts = 0
        let timer: ReturnType<typeof setTimeout>
        const tick = async () => {
            if (cancelled) return
            if (paymentRef.current || attempts >= 6) { setAutoRequesting(false); return }
            attempts++
            try { await refresh() } catch { /* ignore transient */ }
            if (cancelled) return
            timer = setTimeout(tick, 2500)
        }
        timer = setTimeout(tick, 2500)
        return () => { cancelled = true; clearTimeout(timer); setAutoRequesting(false) }
    }, [autoAuthorize, refresh])

    // Errors shown in the banner. For an "incomplete" guarantee, derive them FRESH
    // from the current draft (never the stored snapshot) so the wording always
    // matches the live rules and clears as fields are filled. Otherwise use the
    // errors set imperatively by the request flow. Derived (no setState) — safe.
    const bannerErrors = useMemo(() => {
        if (payment?.status === 'incomplete' && draftOrder) {
            return validateB2BFields(draftOrder).errors
        }
        return validationErrors
    }, [payment?.status, draftOrder, validationErrors])

    // Auto-refresh when page regains focus (e.g. returning from Shopify admin edit)
    useEffect(() => {
        const handleFocus = () => refresh()
        window.addEventListener('focus', handleFocus)
        return () => window.removeEventListener('focus', handleFocus)
    }, [refresh])

    // Refresh status from BNPLX if payment exists and not terminal
    useEffect(() => {
        if (!payment?.paymentId || TERMINAL_SUCCESS.includes(payment.status)) return
        paymentApi.getStatus(payment.paymentId).then(result => {
            if (result.status !== payment.status) {
                const updated = { ...payment, status: result.status }
                setPayment(updated)
                metafield.write(updated)
            }
        }).catch((err) => {
            console.warn('[bnplx] status refresh failed:', err.message)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.paymentId])

    // Fill in the real order link for drafts completed outside our app (e.g. BCM),
    // where the order id isn't on the metafield but is in our DB. Display-only.
    useEffect(() => {
        if (!payment?.paymentId || payment.orderId) return
        paymentApi.getLinkedOrder(payment.paymentId).then(o => {
            if (o.orderId) {
                setPayment(prev => prev ? { ...prev, orderId: o.orderId!, orderName: o.orderName || prev.orderName } : prev)
            }
        }).catch((err) => {
            console.warn('[bnplx] linked order fetch failed:', err.message)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.paymentId, payment?.orderId])

    // Fetch invoice URL from DB for captured payments
    useEffect(() => {
        if (!payment?.paymentId || !TERMINAL_SUCCESS.includes(payment.status)) return
        paymentApi.getInvoiceUrl(payment.paymentId).then(res => {
            if (res.invoiceUrl) setInvoiceUrl(res.invoiceUrl)
            if (res.proxyUrl) setProxyUrl(res.proxyUrl)
        }).catch((err) => {
            console.warn('[bnplx] invoice URL fetch failed:', err.message)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.paymentId, payment?.status])

    const handleRequest = useCallback(async () => {
        if (!draftOrder) return
        setValidationErrors([])
        setMessage(null)

        const total = draftOrder.totalPriceSet.shopMoney
        const base = { amount: total.amount, currency: total.currencyCode }

        // Pre-flight validation. A miss here means we NEVER call BNPLX — this is
        // "incomplete", not a connector decline. Persist it so the badge + any
        // external sync (bnplx_status attribute) reflect the real cause.
        const validation = validateB2BFields(draftOrder)
        if (!validation.valid) {
            setValidationErrors(validation.errors)
            const incomplete: PaymentMetafield = {
                paymentId: '', status: 'incomplete', ...base,
                error: validation.errors.join('; '),
            }
            setPayment(incomplete)
            metafield.write(incomplete).catch(() => {})
            return
        }

        setActiveAction('requesting')

        // Finalize a payment after create/confirm: handle a connector decline,
        // poll a processing payment, or land on success.
        const finalize = async (p: PaymentMetafield) => {
            if (TERMINAL_FAILURE.includes(p.status)) {
                setMessage({ tone: 'critical', text: 'Guarantee declined.' })
                return
            }
            if (needsPolling(p.status)) {
                setMessage({ tone: 'warning', text: 'Authorizing...' })
                const polled = await paymentApi.pollUntilResolved(p.paymentId)
                if (!polled) {
                    setMessage({ tone: 'warning', text: 'Still processing. Refresh later.' })
                    return
                }
                const updated = { ...p, status: polled.status }
                setPayment(updated)
                await metafield.write(updated)
                setMessage(TERMINAL_FAILURE.includes(polled.status)
                    ? { tone: 'critical', text: 'Guarantee declined.' }
                    : { tone: 'success', text: 'Authorized — ready for invoicing' })
                return
            }
            setMessage({ tone: 'success', text: 'Authorized — ready for invoicing' })
        }

        try {
            // Reconcile-then-decide: a payment may already exist (server
            // auto-authorize, or a prior attempt). Read its TRUE status and
            // branch — never blind re-confirm (that duplicates the connector call).
            let currentPayment: PaymentMetafield | null = null
            try {
                const fresh = await fetchDraftOrder(id!)
                const existing = parseMetafield(fresh.metafield)
                if (existing?.paymentId) {
                    const reconciled = await paymentApi.getStatus(existing.paymentId)
                    const status = reconciled.status
                    if (TERMINAL_SUCCESS.includes(status) || isCapturable(status)) {
                        // Already authorized/invoiced — adopt & show, don't re-run.
                        const adopted = { ...existing, ...base, status }
                        setPayment(adopted)
                        await metafield.write(adopted)
                        setMessage({ tone: 'warning', text: 'This draft is already authorized.' })
                        return
                    }
                    if (isConfirmable(status) || needsPolling(status)) {
                        // Connector never decided (unconfirmed) or still processing
                        // — resume THIS payment instead of creating a new one.
                        currentPayment = { ...existing, ...base, status }
                    }
                    // failed/expired/cancelled → leave null → create a NEW payment.
                }
            } catch {
                // Reconcile failed — fall through to a fresh create.
            }

            // Create a new payment unless we're resuming an existing one.
            if (!currentPayment) {
                // Pass draftOrderGid so the server records the payment -> shop/draft
                // mapping, letting the BNPLX resolution callback sync a pending
                // guarantee's status back to this draft once it resolves.
                const createResult = await paymentApi.createPayment({ ...buildPaymentRequestBody(draftOrder), draftOrderGid })
                currentPayment = { paymentId: createResult.paymentId, status: createResult.status, ...base }
                setPayment(currentPayment)
                await metafield.write(currentPayment)
                // Plant the payment id (+status) on custom attributes so it survives
                // the draft→order conversion and external syncs can read it.
                // Best-effort: a failure here must not abort a valid guarantee.
                try {
                    await writePaymentNoteAttribute(draftOrderGid, createResult.paymentId)
                } catch (attrErr) {
                    console.warn('Failed to write payment note attribute', attrErr)
                }
            }

            // Confirm if the payment still needs it (connector authorization step).
            if (isConfirmable(currentPayment.status)) {
                const confirmResult = await paymentApi.confirmPayment(currentPayment.paymentId, buildConfirmBody(draftOrder))
                currentPayment = { ...currentPayment, status: confirmResult.status }
                setPayment(currentPayment)
                await metafield.write(currentPayment)
            }

            await finalize(currentPayment)
        } catch (err: any) {
            // A thrown BNPLX call error (network / 4xx / 5xx) means BNPLX could
            // not process the request — NO connector decision was made. This is
            // "error" (retryable), NOT a connector decline. Never mark 'failed'.
            const errored: PaymentMetafield = { paymentId: '', status: 'error', ...base }
            setPayment(errored)
            metafield.write(errored).catch(() => {})
            setMessage({ tone: 'critical', text: "Couldn't process the request. Please try again." })
        } finally {
            setActiveAction(null)
        }
    }, [draftOrder, paymentApi, metafield, draftOrderGid, id])

    const handleCapture = useCallback(async () => {
        if (!draftOrder || !payment?.paymentId) return
        setMessage(null)
        setActiveAction('capturing')

        try {
            // Step 1: Capture
            const total = draftOrder.totalPriceSet.shopMoney
            const captureResult = await paymentApi.capturePayment(
                payment.paymentId,
                total.amount,
                draftOrder.name || id!,
            )

            // Step 2: Complete draft order → create Shopify order
            setMessage({ tone: 'warning', text: 'Creating order...' })
            const orderResult = await completeDraftOrder(draftOrderGid)

            // Step 3: Link order to payment for webhook processing
            await paymentApi.linkOrder(
                payment.paymentId, orderResult.legacyResourceId, captureResult.captureId,
                captureResult.invoiceUrl,
            )

            // Step 3b: Set the true captured state on the ORDER's attributes. The
            // order inherited the draft's authorize-time attrs (invoice_status=
            // not_invoiced, possibly a stale payment id) and a completed draft can't
            // be updated — so external systems (BC) would otherwise see stale data.
            // Best-effort: never fail the capture on this.
            try {
                await writeOrderBnplxAttributes(orderResult.orderId, {
                    paymentId: payment.paymentId,
                    status: captureResult.status || 'captured',
                })
            } catch (attrErr) {
                console.warn('Failed to write order BNPLX attributes', attrErr)
            }

            // Step 4: Update metafield
            const updated: PaymentMetafield = {
                ...payment,
                status: captureResult.status || 'captured',
                captureId: captureResult.captureId || undefined,
                orderId: orderResult.legacyResourceId,
                orderName: orderResult.orderName,
            }
            setPayment(updated)
            await metafield.write(updated)

            // Step 5: Show invoice URL if available
            if (captureResult.invoiceUrl) {
                setInvoiceUrl(captureResult.invoiceUrl)
            }
            if (captureResult.proxyUrl) {
                setProxyUrl(captureResult.proxyUrl)
            }

            setMessage({
                tone: 'success',
                text: `Invoiced successfully. Order ${orderResult.orderName} created.`,
            })
        } catch (err: any) {
            setMessage({ tone: 'critical', text: err.message || 'Capture failed' })
        } finally {
            setActiveAction(null)
        }
    }, [draftOrder, payment, paymentApi, metafield, id, draftOrderGid])

    const handleRevoke = useCallback(async () => {
        if (!payment?.paymentId) return
        setMessage(null)
        setActiveAction('revoking')

        try {
            await paymentApi.cancelPayment(payment.paymentId)
            setPayment(null)
            await metafield.clear()
            setMessage({ tone: 'success', text: 'Guarantee revoked' })
        } catch (err: any) {
            setMessage({ tone: 'critical', text: err.message || 'Failed to revoke guarantee' })
        } finally {
            setActiveAction(null)
        }
    }, [payment, paymentApi, metafield])

    const handleRefresh = useCallback(async () => {
        setMessage(null)
        if (payment?.paymentId) {
            try {
                const result = await paymentApi.getStatus(payment.paymentId)
                const updated = { ...payment, status: result.status }
                setPayment(updated)
                await metafield.write(updated)
            } catch (err: any) {
                setMessage({ tone: 'critical', text: 'Failed to refresh status' })
            }
        }
        refresh()
    }, [payment, paymentApi, metafield, refresh])

    // Amount drift detection
    const amountDrift = payment?.amount && draftOrder
        && Math.abs(parseFloat(payment.amount) - parseFloat(draftOrder.totalPriceSet.shopMoney.amount)) > 0.01
        && isCancellable(payment.status)

    if (loading) {
        return (
            <Page title={`Draft Order #${id}`} backAction={{ content: 'Draft Orders', url: '/' }}>
                <Card>
                    <BlockStack gap="400" inlineAlign="center">
                        <Spinner size="large" />
                        <Text as="p" tone="subdued">Loading draft order...</Text>
                    </BlockStack>
                </Card>
            </Page>
        )
    }

    if (loadError || !draftOrder) {
        return (
            <Page title={`Draft Order #${id}`} backAction={{ content: 'Draft Orders', url: '/' }}>
                <Banner tone="critical">{loadError || 'Draft order not found'}</Banner>
            </Page>
        )
    }

    const total = draftOrder.totalPriceSet.shopMoney
    const billing = draftOrder.billingAddress
    const customer = draftOrder.customer
    const b2b = draftOrder.purchasingEntity

    // "Requesting Guarantee" while an authorization call is in flight:
    //  - manual: the request is running AND we haven't landed a decided status yet
    //    (null/confirmable). Once it hits processing/authorized/etc. we hand off to
    //    the real badge (processing -> "Authorizing", no spinner).
    //  - auto: the server-side request is inferred (no guarantee yet + polling).
    const requesting =
        (activeAction === 'requesting' && (payment == null || isConfirmable(payment.status)))
        || (autoRequesting && payment == null)

    return (
        <Page
            title={`Draft Order ${draftOrder.name || '#' + id}`}
            backAction={{ content: 'Draft Orders', url: '/' }}
            secondaryActions={[
                { content: 'Edit Draft Order', onAction: () => window.open(adminUrl(`draft_orders/${id}`), '_top') },
            ]}
        >
            <Layout>
                {(message || bannerErrors.length > 0 || amountDrift) && (
                    <Layout.Section>
                        <BlockStack gap="300">
                            {message && (
                                <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
                                    {message.text}
                                </Banner>
                            )}
                            <ValidationBanner errors={bannerErrors} />
                            {amountDrift && (
                                <AmountDriftBanner
                                    oldAmount={payment!.amount}
                                    newAmount={total.amount}
                                    currency={total.currencyCode}
                                />
                            )}
                        </BlockStack>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <BlockStack gap="400">
                        <Card>
                            <BlockStack gap="300">
                                <PaymentStatus
                                    status={payment?.status || null}
                                    amount={payment?.amount || total.amount}
                                    currency={payment?.currency || total.currencyCode}
                                    paymentId={payment?.paymentId || null}
                                    requesting={requesting}
                                />
                                <ActionButtons
                                    status={payment?.status || null}
                                    amount={total.amount}
                                    currency={total.currencyCode}
                                    onRequest={handleRequest}
                                    onCapture={handleCapture}
                                    onRevoke={handleRevoke}
                                    onRefresh={handleRefresh}
                                    loading={loading}
                                    activeAction={activeAction}
                                    invoiceUrl={invoiceUrl}
                                    proxyUrl={proxyUrl}
                                    paymentId={payment?.paymentId || null}
                                    amountDrift={!!amountDrift}
                                    requesting={requesting}
                                />
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingMd">Order Summary</Text>
                                <OrderSummary draftOrder={draftOrder} />
                            </BlockStack>
                        </Card>
                    </BlockStack>
                </Layout.Section>

                <Layout.Section variant="oneThird">
                    <BlockStack gap="400">
                        <Card>
                            <BlockStack gap="300">
                                <Text as="h2" variant="headingMd">Customer</Text>
                                {b2b?.company && (
                                    <Text as="p" fontWeight="bold">{b2b.company.name}</Text>
                                )}
                                {customer ? (
                                    <Link url={adminUrl(`customers/${customer.id.split('/').pop()}`)} target="_blank">
                                        {billing?.firstName || customer.firstName}{' '}
                                        {billing?.lastName || customer.lastName}
                                    </Link>
                                ) : (
                                    <Text as="p" tone="subdued">No customer assigned</Text>
                                )}
                                <Text as="p" tone="subdued">{draftOrder.email || customer?.email}</Text>
                                <Text as="p" tone="subdued">{draftOrder.phone || customer?.phone || billing?.phone}</Text>
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="300">
                                <Text as="h2" variant="headingMd">Address</Text>
                                {billing ? (
                                    <BlockStack gap="100">
                                        <Text as="p" fontWeight="semibold">Billing</Text>
                                        <Text as="p">
                                            {billing.address1}{billing.address2 ? `, ${billing.address2}` : ''}
                                        </Text>
                                        <Text as="p">
                                            {billing.zip} {billing.city}, {billing.countryCode}
                                        </Text>
                                        {billing.phone && <Text as="p" tone="subdued">{billing.phone}</Text>}
                                    </BlockStack>
                                ) : (
                                    <Text as="p" tone="subdued">No billing address</Text>
                                )}
                                {draftOrder.shippingAddress ? (
                                    <BlockStack gap="100">
                                        <Text as="p" fontWeight="semibold">Shipping</Text>
                                        <Text as="p">{draftOrder.shippingAddress.address1}</Text>
                                        <Text as="p">
                                            {draftOrder.shippingAddress.zip} {draftOrder.shippingAddress.city},{' '}
                                            {draftOrder.shippingAddress.countryCode}
                                        </Text>
                                        {draftOrder.shippingAddress.phone && <Text as="p" tone="subdued">{draftOrder.shippingAddress.phone}</Text>}
                                    </BlockStack>
                                ) : (
                                    <Text as="p" tone="subdued">No shipping address</Text>
                                )}
                            </BlockStack>
                        </Card>
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    )
}
